/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Deep Dive template for Tier 2 sub-pages (/features/{parent}/{child}).
 *
 * Lighter than FeaturePageTemplate — focused on ONE specific capability
 * instead of a broad overview. Used when a parent feature page links
 * down to a deeper topic (e.g. /features/takeoffs/blueprint-editor).
 *
 * Structure:
 *   Breadcrumb → Focused Hero → "Why this matters" → Walkthrough →
 *   Key Details → Sibling Navigation → CTA → Footer
 *
 * ~150 lines of config per page vs ~300 for a hub page.
 */

"use client";

import type { LucideIcon } from "lucide-react";
import { CheckCircle, ArrowRight, ChevronRight } from "lucide-react";
import Link from "next/link";
import MainNavigation from "@/components/layout/main-navigation";
import { Footer } from "@/components/landing/footer";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { CTA, TRUST } from "@/content/landing/constants";
import { FadeIn } from "@/components/landing/primitives/fade-in";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeepDiveWalkthroughStep {
  title: string;
  description: string;
  details: string[];
}

export interface DeepDiveDetail {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface DeepDiveSibling {
  label: string;
  href: string;
  icon: LucideIcon;
  description?: string;
}

export interface DeepDiveConfig {
  /** Parent feature info — used for breadcrumb and context */
  parent: {
    label: string;
    href: string;
  };

  /** Hero section */
  badge: string;
  title: string;
  titleHighlight: string;
  description: string;

  /** "Why this matters" — the contractor pain point this solves */
  painPoint?: {
    title: string;
    description: string;
    bullets: string[];
  };

  /** Focused walkthrough — 2-4 steps showing exactly how this works */
  walkthrough: {
    title: string;
    description?: string;
    steps: DeepDiveWalkthroughStep[];
  };

  /** Key details — 4-6 specific facts, specs, or differentiators */
  details: {
    title: string;
    items: DeepDiveDetail[];
  };

  /** Sibling sub-pages under the same parent — for horizontal navigation */
  siblings?: DeepDiveSibling[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DeepDiveTemplate({ config }: { config: DeepDiveConfig }) {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      {/* Breadcrumb */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20 pt-6">
        <nav
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
          aria-label="Breadcrumb"
        >
          <Link
            href="/features"
            className="hover:text-foreground transition-colors"
          >
            Features
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link
            href={config.parent.href}
            className="hover:text-foreground transition-colors"
          >
            {config.parent.label}
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium">{config.badge}</span>
        </nav>
      </div>

      {/* Hero — simpler than the hub template, no background variant */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <FadeIn>
            <span className="inline-block rounded-full bg-secondary/10 px-3.5 py-1 text-xs font-semibold text-secondary mb-4">
              {config.badge}
            </span>
            <h1 className="text-[clamp(28px,4.5vw,48px)] leading-[1.1] font-bold text-foreground font-display max-w-4xl">
              {config.title}{" "}
              <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
                {config.titleHighlight}
              </span>
            </h1>
            <p className="mt-4 text-[clamp(16px,1.5vw,18px)] leading-[1.6] text-muted-foreground max-w-3xl">
              {config.description}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Pain Point — "Why this matters" */}
      {config.painPoint && (
        <section className="py-12 md:py-16 bg-background-warm">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <FadeIn>
              <div className="max-w-3xl">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 font-display">
                  {config.painPoint.title}
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-6">
                  {config.painPoint.description}
                </p>
                <ul className="space-y-3" role="list">
                  {config.painPoint.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 text-sm">
                      <CheckCircle
                        className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <span className="text-muted-foreground">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Walkthrough — focused step-by-step */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <FadeIn>
            <header className="mb-10 max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground font-display">
                {config.walkthrough.title}
              </h2>
              {config.walkthrough.description && (
                <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                  {config.walkthrough.description}
                </p>
              )}
            </header>
          </FadeIn>

          <div className="space-y-8 md:space-y-12">
            {config.walkthrough.steps.map((step, index) => (
              <FadeIn key={step.title} delay={index * 100}>
                <div className="flex gap-6">
                  {/* Step number */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-foreground mb-2 font-display">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {step.description}
                    </p>
                    <ul className="space-y-2" role="list">
                      {step.details.map((detail) => (
                        <li
                          key={detail}
                          className="flex items-start gap-2 text-sm"
                        >
                          <CheckCircle
                            className="w-4 h-4 text-secondary/70 flex-shrink-0 mt-0.5"
                            aria-hidden="true"
                          />
                          <span className="text-muted-foreground">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Key Details — specs, differentiators */}
      <section className="py-16 md:py-24 bg-background-warm">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 font-display">
              {config.details.title}
            </h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {config.details.items.map((item, index) => {
              const Icon = item.icon;
              return (
                <FadeIn key={item.title} delay={index * 80}>
                  <div className="rounded-2xl border border-border bg-background p-6 h-full">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-secondary" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground mb-2 font-display">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sibling Navigation — other sub-pages under the same parent */}
      {config.siblings && config.siblings.length > 0 && (
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <FadeIn>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground font-display">
                  More on {config.parent.label}
                </h2>
                <Link
                  href={config.parent.href}
                  className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors inline-flex items-center gap-1.5"
                >
                  Back to {config.parent.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </FadeIn>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {config.siblings.map((sibling, index) => {
                const Icon = sibling.icon;
                return (
                  <FadeIn key={sibling.href} delay={index * 80}>
                    <Link
                      href={sibling.href}
                      className="group flex items-center gap-3 rounded-xl border border-border bg-muted/20 p-4 hover:border-secondary/30 hover:shadow-md transition-all"
                    >
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-foreground group-hover:text-secondary transition-colors">
                          {sibling.label}
                        </span>
                        {sibling.description && (
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            {sibling.description}
                          </span>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-secondary transition-colors flex-shrink-0" />
                    </Link>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <MarketingCTASection
        title="See it for yourself"
        description={`${TRUST.STRIP[0]}. No credit card required. Pick your trade and start estimating.`}
        primaryCTA={{ text: CTA.PRIMARY, href: CTA.PRIMARY_HREF }}
        secondaryCTA={{
          text: CTA.SECONDARY_CONTACT,
          href: CTA.CONTACT_HREF,
        }}
        trustIndicators={[TRUST.INLINE]}
      />

      <Footer />
    </div>
  );
}
